import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useCart } from "@/contexts/CartContext";

export default function CartPage() {
  const { items, removeFromCart, updateQuantity, totalPrice, totalItems } = useCart();
  const navigate = useNavigate();
  const shipping = totalPrice >= 500 ? 0 : 49;
  const tax = totalPrice * 0.08;
  const orderTotal = totalPrice + shipping + tax;

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <Navbar />
      <main className="pt-[72px]">
        <div className="max-w-7xl mx-auto px-6 py-10">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Shopping Cart
            </h1>
            <p className="text-gray-400 mt-1 text-sm">{totalItems} {totalItems === 1 ? "item" : "items"}</p>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-20 h-20 flex items-center justify-center bg-[#EEF5E8] rounded-full mx-auto mb-5">
                <i className="ri-shopping-bag-line text-3xl text-[#2D5016]"></i>
              </div>
              <h2 className="text-xl font-semibold text-[#1a1a1a] mb-2">Your cart is empty</h2>
              <p className="text-gray-400 text-sm mb-6">Discover our premium outdoor furniture collection</p>
              <Link
                to="/products"
                className="bg-[#2D5016] text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-[#3d6826] transition-colors cursor-pointer whitespace-nowrap"
              >
                Shop Now
              </Link>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Cart Items */}
              <div className="flex-1 space-y-4">
                {items.map(item => (
                  <div key={item.product.id} className="bg-white rounded-2xl p-5 flex flex-col sm:flex-row gap-4">
                    <Link to={`/product/${item.product.id}`} className="flex-shrink-0">
                      <div className="w-24 h-24 rounded-xl overflow-hidden bg-[#F9F7F3]">
                        <img
                          src={item.product.images[0]}
                          alt={item.product.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </Link>
                    <div className="flex-1 flex flex-col sm:flex-row gap-4 justify-between">
                      <div className="flex-1">
                        <span className="text-[#4A7C2F] text-xs font-medium uppercase tracking-wide">{item.product.category}</span>
                        <Link to={`/product/${item.product.id}`}>
                          <h3 className="text-[#1a1a1a] font-semibold text-sm mt-0.5 hover:text-[#2D5016] transition-colors cursor-pointer">
                            {item.product.name}
                          </h3>
                        </Link>
                        <p className="text-gray-400 text-xs mt-1">{item.product.material}</p>
                      </div>
                      <div className="flex items-center gap-6">
                        {/* Quantity */}
                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer text-gray-500 text-sm"
                          >
                            <i className="ri-subtract-line"></i>
                          </button>
                          <span className="w-8 text-center text-sm font-semibold text-[#1a1a1a]">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer text-gray-500 text-sm"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>
                        {/* Price */}
                        <div className="text-right min-w-[80px]">
                          <div className="font-bold text-[#1a1a1a] text-base">${(item.product.price * item.quantity).toFixed(2)}</div>
                          <div className="text-gray-400 text-xs">${item.product.price} each</div>
                        </div>
                        {/* Remove */}
                        <button
                          onClick={() => removeFromCart(item.product.id)}
                          className="text-gray-300 hover:text-red-400 transition-colors cursor-pointer flex items-center justify-center w-8 h-8"
                        >
                          <i className="ri-delete-bin-line text-base"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                <Link to="/products" className="flex items-center gap-2 text-[#2D5016] text-sm font-medium hover:gap-3 transition-all cursor-pointer mt-4">
                  <i className="ri-arrow-left-line"></i> Continue Shopping
                </Link>
              </div>

              {/* Order Summary */}
              <div className="w-full lg:w-[360px] flex-shrink-0">
                <div className="bg-white rounded-2xl p-6 sticky top-24">
                  <h2 className="text-lg font-bold text-[#1a1a1a] mb-6">Order Summary</h2>
                  <div className="space-y-3 mb-5">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Subtotal ({totalItems} items)</span>
                      <span className="font-medium text-[#1a1a1a]">${totalPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Shipping</span>
                      <span className={`font-medium ${shipping === 0 ? "text-[#2D5016]" : "text-[#1a1a1a]"}`}>
                        {shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Tax (8%)</span>
                      <span className="font-medium text-[#1a1a1a]">${tax.toFixed(2)}</span>
                    </div>
                    {totalPrice < 500 && (
                      <div className="bg-[#EEF5E8] rounded-lg p-3 text-xs text-[#2D5016]">
                        Add ${(500 - totalPrice).toFixed(2)} more for free shipping!
                      </div>
                    )}
                  </div>
                  <div className="border-t border-gray-100 pt-4 mb-6">
                    <div className="flex justify-between">
                      <span className="font-bold text-[#1a1a1a]">Total</span>
                      <span className="font-bold text-xl text-[#1a1a1a]">${orderTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/checkout")}
                    className="w-full bg-[#2D5016] hover:bg-[#3d6826] text-white font-semibold py-4 rounded-full transition-colors cursor-pointer text-sm tracking-wide whitespace-nowrap"
                  >
                    Proceed to Checkout
                  </button>
                  <div className="flex items-center justify-center gap-2 mt-4 text-gray-400 text-xs">
                    <i className="ri-lock-line"></i>
                    <span>Secure checkout powered by Stripe</span>
                  </div>
                  {/* Payment Icons */}
                  <div className="flex items-center justify-center gap-3 mt-3">
                    {["ri-visa-line", "ri-mastercard-line", "ri-paypal-line"].map(icon => (
                      <div key={icon} className="w-8 h-8 flex items-center justify-center">
                        <i className={`${icon} text-gray-300 text-xl`}></i>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
